import React, { useEffect, useMemo } from 'react';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import ComingSoonOverlay from '../ui/coming-soon-overlay';
import waitlistService from '../../services/waitlistService';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
// import { toast } from 'react-hot-toast';

interface ComingSoonWrapperProps {
  children: React.ReactNode;
  feature: 'publishing' | 'scheduling' | 'social_analytics' | 'social_connections' | 'one_click_creator' | 'video_generation' | 'ar_generation' | '3d_generation';
  title: string;
  description: string;
  features?: string[];
  expectedDate?: string;
  className?: string;
  icon?: 'rocket' | 'zap' | 'bot';
}

export const ComingSoonWrapper: React.FC<ComingSoonWrapperProps> = ({
  children,
  feature,
  title,
  description,
  features = [],
  expectedDate,
  className = '',
  icon = 'rocket'
}) => {
  const { flags, loading } = useFeatureFlags();
  const { user } = useAuth();
  const isFeatureEnabled = flags[feature];
  const showComingSoon = flags.SHOW_COMING_SOON_FEATURES;
  
  // Check if user is a beta tester from their profile
  const isBetaTester = user?.is_beta_tester || false;

  // Beta testers get full access - no polling or refetching needed
  // Feature flags are loaded once on app start



  // Show loading state to prevent flash
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const handleWaitlistJoin = async (email: string) => {
    try {
      await waitlistService.joinSocialFeaturesWaitlist(email, [feature]);
      // toast.success('Successfully joined the waitlist! We\'ll notify you when it\'s ready.');
      console.log('Successfully joined the waitlist!');
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      // toast.error('Failed to join waitlist. Please try again.');
      throw error;
    }
  };

  // If feature is enabled OR user is a beta tester, show the actual content normally
  // EXCEPTION: video_generation, ar_generation, and 3d_generation are always locked (even for beta testers)
  if ((isFeatureEnabled || isBetaTester) && !['video_generation', 'ar_generation', '3d_generation'].includes(feature)) {
    return <>{children}</>;
  }

  // If we should show coming soon, show content with blur overlay
  if (showComingSoon) {
    return (
      <div className={`relative ${className}`}>
        {/* Blurred content */}
        <div className="filter blur-sm brightness-90 pointer-events-none select-none">
          {children}
        </div>
        
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-purple-50/20 to-pink-50/30 dark:from-blue-900/20 dark:via-purple-900/10 dark:to-pink-900/20"></div>
        
        {/* Coming soon overlay */}
        <ComingSoonOverlay
          title={title}
          description={description}
          features={features}
          expectedDate={expectedDate}
          onWaitlistJoin={handleWaitlistJoin}
          icon={icon}
        />
      </div>
    );
  }

  // Otherwise, don't render anything (completely hide the feature)
  return null;
};

export default ComingSoonWrapper;